import {
  setShowAdditionalFreeSpins,
  setCriticalAnimationPlaying,
} from "../../../../State/actions";
import { Settings } from "../../../../settings";
import { PixiSettings } from "../../../../pixiSettings";
import TickerTimeout from "../../../../utils/TickerTimeout";
import { soundManager } from "../../../../SoundManager";
import { SOUND_NAME } from "../../../../sounds";
import FeatureIntroCard, {
  fitCardText,
  splitCountSentence,
} from "../../FeatureIntro/FeatureIntroCard";

/**
 * AdditionalFreeSpinsController — "you won more free spins" (GDD §9,
 * Retrigger), shown mid-feature.
 *
 * Draws the FS intro card (FeatureIntroCard, `free_spins`) with four lines:
 *
 *   congrats  CONGRATULATIONS!
 *   lead      YOU WON
 *   count     +N
 *   label     ADDITIONAL FREE SPINS
 *
 * and plays in -> loop x RETRIGGER_LOOPS -> out, then closes itself. No tap.
 *
 * Beats (each fires once per play):
 *   count shown   halfway through `in`   the win sound
 *   in end        the card is up         hooks.onIdle (the caller updates the
 *                                         spin counter behind the card)
 *   out start                            the outro sound
 *   closed        after `out` + overlay   hooks.onClosed (the round resumes)
 */

// const RETRIGGER_LOOPS = 1;
// ── Show the info and go (2026-10-14: "appear, pulse 2 times and go away …
// it breaks the flow") ── no slow `loop`: `in` ends on a pop pulse and `out`
// opens with one, so in -> out is the two pulses, ~2 s instead of ~3.7 s. The
// clips are FeatureIntroCard's, shared with the free-spins intro — untouched.
const RETRIGGER_LOOPS = 0;
// The FS card's arrangement: the count above the board, the label under it.
const COPY_LINES = { above: ["congrats", "lead", "count"], below: ["label"] };

// Card scale per device, on top of the symbol-size scale; `offset` is a
// multiple of symbolHeight, pushed UP from the screen centre.
const DEVICE_MULTIPLIERS = {
  MOBILE_SMALL_PORTRAIT: { scale: 1.0, offset: 0.0 },
  MOBILE_SMALL_LANDSCAPE: { scale: 1.0, offset: 0.0 },
  MOBILE_PORTRAIT: { scale: 1.0, offset: 0.0 },
  MOBILE_LANDSCAPE: { scale: 1.0, offset: 0.0 },
  TABLET_PORTRAIT: { scale: 1.0, offset: 0.0 },
  TABLET_LANDSCAPE: { scale: 1.0, offset: 0.0 },
  DESKTOP: { scale: 0.9, offset: 0.0 },
};

class AdditionalFreeSpinsController {
  constructor(container, dimensionsContext, dispatch, symbolHeight, overlayRef, t) {
    this.container = container;
    this.dimensionsContext = dimensionsContext;
    this.dimensions = dimensionsContext?.dimensions;
    this.dispatch = dispatch;
    this.symbolHeight = symbolHeight;
    this.overlayRef = overlayRef;
    this.t = t;

    this.card = null;
    this.isPlaying = false;
    this.initialized = false;

    this.texts = {};

    this._spinCount = 0;
    this._onIdle = null;
    this._onClosed = null;
    this._fired = { countShown: false, inEnd: false, outStart: false, done: false };
    this.countTimeout = null;
    this.completeTimeout = null;
    this._showRAF = null;

    this._configCache = null;
    this._lastDevice = null;
    this._lastOrientation = null;
    this._lastIsMobileSmall = null;

    this.initializeCard();
  }

  // ── Sizing ──

  getDeviceMultipliers() {
    const { device, orientation, isMobileSmall } = this.dimensionsContext || {};
    if (
      this._configCache !== null &&
      this._lastDevice === device &&
      this._lastOrientation === orientation &&
      this._lastIsMobileSmall === isMobileSmall
    ) {
      return this._configCache;
    }
    const isPortrait = orientation === PixiSettings.ORIENTATION.PORTRAIT;
    let key = "DESKTOP";
    if (device === PixiSettings.DEVICE.MOBILE) {
      if (isMobileSmall) key = isPortrait ? "MOBILE_SMALL_PORTRAIT" : "MOBILE_SMALL_LANDSCAPE";
      else key = isPortrait ? "MOBILE_PORTRAIT" : "MOBILE_LANDSCAPE";
    } else if (device === PixiSettings.DEVICE.TABLET) {
      key = isPortrait ? "TABLET_PORTRAIT" : "TABLET_LANDSCAPE";
    }
    this._configCache = DEVICE_MULTIPLIERS[key];
    this._lastDevice = device;
    this._lastOrientation = orientation;
    this._lastIsMobileSmall = isMobileSmall;
    return this._configCache;
  }

  _isLandscape() {
    return this.dimensionsContext?.orientation !== PixiSettings.ORIENTATION.PORTRAIT;
  }

  applyScaleAndCenter() {
    if (!this.card || !this.dimensions) return;
    const nativeSize = Settings.SYMBOL_HEIGHT;
    const targetSize = this.symbolHeight || nativeSize;
    const { scale: deviceScale, offset } = this.getDeviceMultipliers();
    const scale = Math.max(0.1, Math.min(targetSize / nativeSize, 2)) * deviceScale;
    this.card.view.scale.set(scale);
    // The background for this orientation (FeatureIntroCard.setLandscape).
    this.card.setLandscape?.(this._isLandscape());
    // The parent container sits at the screen centre.
    this.card.view.position.set(0, -(this.symbolHeight || 0) * offset);
  }

  updateDimensions(dimensionsContext, symbolHeight) {
    const oldDims = this.dimensionsContext?.dimensions;
    const newDims = dimensionsContext?.dimensions;
    const changed =
      !oldDims ||
      oldDims.width !== newDims?.width ||
      oldDims.height !== newDims?.height ||
      this.dimensionsContext?.device !== dimensionsContext?.device ||
      this.dimensionsContext?.orientation !== dimensionsContext?.orientation ||
      this.symbolHeight !== symbolHeight;
    if (!changed) return;
    this.dimensionsContext = dimensionsContext;
    this.dimensions = dimensionsContext?.dimensions;
    this.symbolHeight = symbolHeight;
    this._configCache = null;
    this.applyScaleAndCenter();
  }

  // ── Card ──

  initializeCard() {
    try {
      this.card = new FeatureIntroCard("free_spins");
      this.card.view.alpha = 0;
      this.card.view.zIndex = 20;
      this.container.addChild(this.card.view);
      this.card.warmUp();
      this.initialized = true;
      this.initializeTexts();
      this.applyScaleAndCenter();
    } catch (error) {
      console.warn("[AdditionalFreeSpins] Init failed:", error);
    }
  }

  initializeTexts() {
    const lines = this._lines();
    const landscape = this._isLandscape();
    this.card.setCopyLines(COPY_LINES);
    for (const name of [...COPY_LINES.above, ...COPY_LINES.below]) {
      this.texts[name] = this.card.addText(name, lines[name], landscape);
    }
  }

  /**
   * CONGRATULATIONS! / YOU WON / +n / ADDITIONAL FREE SPINS — the shared
   * sentence, split on its count. (A locale that words it as one line — no
   * count line — is split by splitCountSentence's own fallback; the game's
   * popup_additional_free_spins copy is gone, 2026-10-13.)
   */
  _lines() {
    const count = this._spinCount ?? 0;
    // let lines = splitCountSentence(
    const lines = splitCountSentence(
      this.t("congrats_you_won_additional_freespins", { count }),
      count,
    );
    // if (!lines.found) {
    //   lines = splitCountSentence(this.t("popup_additional_free_spins", { count }), count);
    // }
    // The shared sentence only (2026-10-13): popup_additional_free_spins was a
    // game-local copy, removed from translations.json. A locale without a count
    // line still splits (splitCountSentence's own fallback).
    return { ...lines, count: `+${count}` };
  }

  // ── Content ──

  /** The card is the same at every level; kept for the level-up sequence's API. */
  setLevel() {}

  /** The card is the same whether a box closed or not; kept for the same API. */
  setNoBoxClosed() {}

  /** How many spins were added. */
  setSpinCount(count) {
    this._spinCount = count;
    const lines = this._lines();
    const landscape = this._isLandscape();
    for (const [name, textObj] of Object.entries(this.texts)) {
      if (!textObj) continue;
      textObj.text = lines[name];
      fitCardText(textObj, landscape);
    }
  }

  // ── Sequence ──

  /**
   * Open it: fade the overlay in, play in -> loop -> out, close.
   *
   * Every exit ends the caller's wait — the level-up sequence awaits onClosed
   * while holding criticalAnimationPlaying.
   *
   * @param {object}   [hooks]
   * @param {Function} [hooks.onIdle]    the card is up (end of `in`)
   * @param {Function} [hooks.onClosed]  the popup is gone
   */
  startSequence(hooks = {}) {
    if (this.isPlaying) {
      // A popup is on screen and owns the flag; only this caller is released.
      console.warn("[AdditionalFreeSpins] startSequence refused — already playing");
      hooks.onClosed?.();
      return;
    }
    this._onIdle = hooks.onIdle ?? null;
    this._onClosed = hooks.onClosed ?? null;
    if (!this.card) {
      console.warn("[AdditionalFreeSpins] startSequence refused — no card");
      this._releaseCaller();
      return;
    }

    this.isPlaying = true;
    this._fired = { countShown: false, inEnd: false, outStart: false, done: false };
    this._clearTimeouts();

    this.overlayRef?.current?.fadeInBackground();
    this.dispatch?.(setCriticalAnimationPlaying(true));
    this.applyScaleAndCenter();

    const inMs = this.card.duration("in");
    const loopMs = this.card.duration("loop") * RETRIGGER_LOOPS;
    const outMs = this.card.duration("out");

    this.card.play("in", {
      onComplete: () => {
        if (!this.isPlaying) return;
        this._beatInEnd();
        const toOut = () => {
          if (!this.isPlaying) return;
          this._beatOutStart();
          this.card.play("out", { onComplete: () => this._finishOnce() });
        };
        // this.card.play("loop", {
        //   loop: RETRIGGER_LOOPS,
        //   onComplete: () => {
        //     if (!this.isPlaying) return;
        //     this._beatOutStart();
        //     this.card.play("out", { onComplete: () => this._finishOnce() });
        //   },
        // });
        if (RETRIGGER_LOOPS > 0) {
          this.card.play("loop", { loop: RETRIGGER_LOOPS, onComplete: toOut });
        } else {
          toOut();
        }
      },
    });
    // soundManager.play(SOUND_NAME.additional_free_spins_intro);
    // The intros' own sound (2026-10-22): the retrigger is the free spins /
    // Hold & Win intro's dialog now, so it sounds like them.
    soundManager.play(SOUND_NAME.free_spin_intro);
    if (this._showRAF) cancelAnimationFrame(this._showRAF);
    this._showRAF = requestAnimationFrame(() => {
      this._showRAF = null;
      if (this.isPlaying && this.card) this.card.view.alpha = 1;
    });

    this.countTimeout = new TickerTimeout(() => this._beatCountShown(), inMs * 0.5);
    // Net: the round never waits on a clip that did not finish.
    this.completeTimeout = new TickerTimeout(
      () => this._finishOnce(),
      inMs + loopMs + outMs + 100,
    );
  }

  _beatCountShown() {
    if (!this.isPlaying || this._fired.countShown) return;
    this._fired.countShown = true;
    // soundManager.play(SOUND_NAME.additional_free_spins_win);
  }

  _beatInEnd() {
    if (!this.isPlaying || this._fired.inEnd) return;
    this._fired.inEnd = true;
    const onIdle = this._onIdle;
    this._onIdle = null;
    onIdle?.();
  }

  _beatOutStart() {
    if (!this.isPlaying || this._fired.outStart) return;
    this._fired.outStart = true;
    // soundManager.play(SOUND_NAME.additional_free_spins_outro);
    // soundManager.play(SOUND_NAME.free_spin_intro_close);
    // No close sound (2026-10-22), as the intros.
  }

  _finishOnce() {
    if (!this.isPlaying || this._fired.done) return;
    // The counter is never left short, even if `in` was cut.
    this._beatInEnd();
    this.finishAnimation();
  }

  /**
   * End the caller's wait without having shown anything. The flag MUST come
   * down: the caller raised `showAdditionalFreeSpins`, and the dialog's tap
   * absorber and key swallow are gated on it.
   */
  _releaseCaller() {
    this.isPlaying = false;
    this._onIdle = null;
    this.dispatch?.(setShowAdditionalFreeSpins(false));
    const closed = this._onClosed;
    this._onClosed = null;
    closed?.();
  }

  finishAnimation() {
    if (!this.card || !this.initialized) {
      this.dispatch?.(setCriticalAnimationPlaying(false));
      this._releaseCaller();
      return;
    }

    this.isPlaying = false;
    this._fired.done = true;
    this.card.stop();
    this.card.view.alpha = 0;
    if (this._showRAF) {
      cancelAnimationFrame(this._showRAF);
      this._showRAF = null;
    }
    this._clearTimeouts();
    this.dispatch?.(setCriticalAnimationPlaying(false));

    // The flag clears with the overlay, and the round resumes after both.
    this.overlayRef?.current?.fadeOutBackground(() => {
      this.dispatch?.(setShowAdditionalFreeSpins(false));
      const onClosed = this._onClosed;
      this._onClosed = null;
      onClosed?.();
    });
  }

  _clearTimeouts() {
    for (const key of ["countTimeout", "completeTimeout"]) {
      if (this[key]) {
        this[key].clear();
        this[key] = null;
      }
    }
  }

  cleanup() {
    // Release whatever waits on this popup before it goes.
    const onClosed = this._onClosed;
    this._onIdle = null;
    this._onClosed = null;
    this.dispatch?.(setShowAdditionalFreeSpins(false));
    onClosed?.();

    if (this._showRAF) {
      cancelAnimationFrame(this._showRAF);
      this._showRAF = null;
    }
    this._clearTimeouts();

    if (this.card) {
      if (this.container?.children.includes(this.card.view)) {
        this.container.removeChild(this.card.view);
      }
      this.card.destroy();
      this.card = null;
    }
    this.texts = {};
    this.initialized = false;
  }
}

export default AdditionalFreeSpinsController;
